import type { Audit, PaginatedList } from '@local/common-lib';
import {
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { lower, sentence } from 'case';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import { RelativeDate } from '../../components/date/index.js';
import { FormatUserAgent } from './user-agent.js';

interface Props {
  audits: PaginatedList<Audit.Type>;
}

const formatReason = (key: unknown): string => {
  return typeof key === 'string' ? lower(key, ' ') : '';
};

const AuditDetail: FC<{ audit: Audit.Type }> = ({ audit }) => {
  switch (audit.event) {
    case 'sign-in-success':
      return (
        <Stack direction="row" spacing={1}>
          <Typography>{sentence(audit.event)}</Typography>
          <Typography color="text.secondary">from</Typography>
          <Typography>
            {audit.request.client.ips.length ? (
              audit.request.client.ips[0]
            ) : (
              <i>unknown</i>
            )}
          </Typography>
        </Stack>
      );
    case 'session-revoked':
    case 'locked-out':
    case 'sign-in-failure':
      return (
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontWeight: 'bold' }}>
            Sign in failed - {formatReason(audit.data?.reason)}
          </Typography>
          <Typography color="text.secondary">from</Typography>
          <Typography>
            {audit.request.client.ips.length ? (
              audit.request.client.ips[0]
            ) : (
              <i>unknown</i>
            )}
          </Typography>
        </Stack>
      );
    default:
      return (
        <Stack direction="row" spacing={1}>
          <Typography sx={{ fontWeight: 'bold' }}>
            {sentence(audit.event)}
          </Typography>
          <Typography color="text.secondary">from</Typography>
          <Typography>
            {audit.request.client.ips.length ? (
              audit.request.client.ips[0]
            ) : (
              <i>unknown</i>
            )}
          </Typography>
        </Stack>
      );
  }
};

export const NarrowAuditList: FC<Props> = ({ audits }) => {
  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>Event details</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {audits.items.map((row) => (
          <TableRow
            key={row._id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              <Stack direction="column" spacing={0}>
                <RelativeDate date={DateTime.fromJSDate(row.when)} />
                <AuditDetail audit={row} />
                <FormatUserAgent agent={row.request.client.userAgent} />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export const WideAuditList: FC<Props> = ({ audits }) => {
  return (
    <Table aria-label="search results">
      <TableHead>
        <TableRow>
          <TableCell>When</TableCell>
          <TableCell>Info</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {audits.items.map((row) => (
          <TableRow
            key={row._id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCell component="th" scope="row">
              <RelativeDate date={DateTime.fromJSDate(row.when)} />
            </TableCell>
            <TableCell>
              <Stack direction="column" spacing={0}>
                <AuditDetail audit={row} />
                <FormatUserAgent agent={row.request.client.userAgent} />
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
